package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ClubAdminRepository
import com.arena_web.ui.components.widgets.SortDirection
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ClientsUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val clubs: List<ClubDto> = emptyList(),
    val clubId: String? = null,

    // Clients data
    val clients: List<ClubClientDto> = emptyList(),
    val total: Int = 0,
    val page: Int = 0,
    val pageSize: Int = 10,

    // Stats
    val activeCount: Int = 0,
    val totalBookings: Long = 0,
    val totalRevenue: String = "0",

    // Filters
    val search: String = "",
    val statusFilter: String? = null,
    val sortBy: String = "name",
    val sortAscending: Boolean = true,

    // Client detail popup
    val selectedClientCard: ClubClientCardDto? = null,
    val selectedClientBookings: List<BookingDto> = emptyList(),
    val isDetailLoading: Boolean = false,
    val noteText: String = "",
)

class ClientsViewModel(
    private val clubAdminRepository: ClubAdminRepository
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ClientsUiState())
    val uiState: StateFlow<ClientsUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadClubs()
    }

    private fun loadClubs() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            clubAdminRepository.getMyClubs().onSuccess { clubs ->
                _uiState.update { it.copy(clubs = clubs) }
                val savedId = Storage.get(StorageKeys.SELECTED_CLUB_ID)
                val club = clubs.find { it.id == savedId } ?: clubs.firstOrNull()
                if (club != null) {
                    Storage.save(StorageKeys.SELECTED_CLUB_ID, club.id)
                    _uiState.update { it.copy(clubId = club.id) }
                    loadClients(club.id)
                } else {
                    _uiState.update { it.copy(isLoading = false) }
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }

    private fun loadClients(clubId: String) {
        // Read state BEFORE launching coroutine to avoid stale reads
        val state = _uiState.value
        val params = buildMap {
            if (state.search.isNotBlank()) put("search", state.search)
            if (state.statusFilter != null) put("status", state.statusFilter)
            put("sortBy", state.sortBy)
            put("sortDirection", if (state.sortAscending) "asc" else "desc")
            put("page", state.page.toString())
            put("size", state.pageSize.toString())
        }

        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }

            clubAdminRepository.getClients(clubId, params).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        clients = page.clients,
                        total = page.total,
                        page = page.page,
                        activeCount = page.activeCount,
                        totalBookings = page.totalBookings,
                        totalRevenue = page.totalRevenue,
                    )
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }

    fun switchClub(clubId: String) {
        val club = _uiState.value.clubs.find { it.id == clubId } ?: return
        Storage.save(StorageKeys.SELECTED_CLUB_ID, clubId)
        _uiState.update {
            it.copy(
                clubId = clubId,
                clients = emptyList(),
                page = 0,
                search = "",
                statusFilter = null,
                sortBy = "name",
                sortAscending = true,
                isLoading = true
            )
        }
        loadClients(clubId)
    }

    fun onSearchChanged(query: String) {
        _uiState.update { it.copy(search = query, page = 0) }
        val clubId = _uiState.value.clubId ?: return
        loadClients(clubId)
    }

    fun onStatusFilterChanged(status: String?) {
        _uiState.update { it.copy(statusFilter = status, page = 0) }
        val clubId = _uiState.value.clubId ?: return
        loadClients(clubId)
    }

    fun onSortChanged(column: String, direction: SortDirection) {
        val current = _uiState.value
        if (direction == SortDirection.None) {
            _uiState.update { it.copy(sortBy = "name", sortAscending = true, page = 0) }
        } else {
            _uiState.update { it.copy(sortBy = column, sortAscending = direction == SortDirection.Ascending, page = 0) }
        }
        val clubId = current.clubId ?: return
        loadClients(clubId)
    }

    fun onPageChanged(page: Int) {
        _uiState.update { it.copy(page = page) }
        val clubId = _uiState.value.clubId ?: return
        loadClients(clubId)
    }

    fun deleteClient(clientId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.blockClient(clubId, clientId, BlockClientRequest()).onSuccess {
                loadClients(clubId)
            }
        }
    }

    // =========================================================================
    // Client detail popup
    // =========================================================================

    fun openClientDetail(clientId: String) {
        val clubId = _uiState.value.clubId ?: return
        _uiState.update { it.copy(isDetailLoading = true, noteText = "") }

        viewModelScope.launch {
            clubAdminRepository.getClient(clubId, clientId).onSuccess { card ->
                _uiState.update { it.copy(selectedClientCard = card, isDetailLoading = false) }
            }.onFailure {
                _uiState.update { it.copy(isDetailLoading = false) }
            }

            clubAdminRepository.getClientBookings(clubId, clientId).onSuccess { bookings ->
                _uiState.update { it.copy(selectedClientBookings = bookings) }
            }
        }
    }

    fun closeClientDetail() {
        _uiState.update {
            it.copy(
                selectedClientCard = null,
                selectedClientBookings = emptyList(),
                noteText = ""
            )
        }
    }

    fun onNoteTextChanged(text: String) {
        _uiState.update { it.copy(noteText = text) }
    }

    fun addNote() {
        val state = _uiState.value
        val clubId = state.clubId ?: return
        val clientId = state.selectedClientCard?.userId ?: return
        val text = state.noteText.trim()
        if (text.isBlank()) return

        viewModelScope.launch {
            clubAdminRepository.addClientNote(clubId, clientId, AddClientNoteRequest(text)).onSuccess { note ->
                _uiState.update {
                    val updatedCard = it.selectedClientCard?.copy(
                        notes = it.selectedClientCard.notes + note
                    )
                    it.copy(selectedClientCard = updatedCard, noteText = "")
                }
            }
        }
    }

    fun blockClient(clientId: String) {
        val clubId = _uiState.value.clubId ?: return
        viewModelScope.launch {
            clubAdminRepository.blockClient(clubId, clientId, BlockClientRequest()).onSuccess {
                closeClientDetail()
                loadClients(clubId)
            }
        }
    }

    val totalPages: Int
        get() {
            val state = _uiState.value
            return if (state.total <= 0) 1
            else (state.total + state.pageSize - 1) / state.pageSize
        }
}
